import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import PaymentsIcon from "@mui/icons-material/Payments";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import GroupsIcon from "@mui/icons-material/Groups";
import PrintIcon from "@mui/icons-material/Print";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../modals/AnimatedModal";

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.08 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const DeliveryCenterSetupModal = ({ isOpen, onClose }) => {
  const theme = useTheme();
  const history = useHistory();

  useEffect(() => {
    console.log("isOpen", isOpen);
  }, [isOpen]);

  const handleSetupNow = () => {
    history.push("/delivery-center");
    onClose();
  };

  const essentialConfigs = [
    { icon: PhoneIcon, title: "Contato" },
    { icon: BusinessIcon, title: "Endereço" },
  ];

  const advancedFeatures = [
    { icon: PaymentsIcon, title: "Pagamentos" },
    { icon: TrackChangesIcon, title: "Raios" },
    { icon: LocalShippingIcon, title: "Integrações" },
    { icon: GroupsIcon, title: "Equipe" },
    { icon: PrintIcon, title: "Impressoras" },
    { icon: ReceiptIcon, title: "Fiscal" },
  ];

  return (
    <AnimatedModal isOpen={isOpen} width="90%" maxWidth="540px" zIndex={9999}>
      <ModalHeader
        title="Configure sua Central"
        subtitle="Dados essenciais para começar"
      />

      <ModalContent>
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Hero — horizontal glass banner */}
          <motion.div variants={fadeUp}>
            <Box
              sx={{
                p: 2.5,
                mb: 2.5,
                borderRadius: "14px",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                gap: 2.5,
                background: `
                  linear-gradient(
                    135deg,
                    ${theme.palette.primary.dark} 0%,
                    ${theme.palette.primary.main} 30%,
                    ${theme.palette.info.main} 60%,
                    ${alpha(theme.palette.success.main, 0.7)} 100%
                  )
                `,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: `
                    radial-gradient(ellipse 80% 60% at 20% 80%, ${alpha(theme.palette.primary.light, 0.4)} 0%, transparent 70%),
                    radial-gradient(ellipse 60% 50% at 80% 20%, ${alpha(theme.palette.info.light, 0.3)} 0%, transparent 70%)
                  `,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  backdropFilter: "blur(40px) saturate(1.2)",
                  background: alpha("#fff", 0.05),
                  borderRadius: "inherit",
                },
              }}
            >
              {/* Orbs */}
              <Box
                sx={{
                  position: "absolute",
                  top: -30,
                  right: -10,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha("#fff", 0.12)} 0%, transparent 70%)`,
                  filter: "blur(2px)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -20,
                  left: 20,
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha(theme.palette.info.light, 0.18)} 0%, transparent 70%)`,
                  filter: "blur(4px)",
                }}
              />

              {/* Icon */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  bgcolor: alpha("#fff", 0.12),
                  border: `1px solid ${alpha("#fff", 0.2)}`,
                  backdropFilter: "blur(12px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: `0 8px 32px ${alpha("#000", 0.12)}`,
                }}
              >
                <RocketLaunchIcon sx={{ fontSize: 22, color: "#fff" }} />
              </Box>

              {/* Text */}
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    lineHeight: 1.3,
                    textShadow: `0 1px 8px ${alpha("#000", 0.15)}`,
                  }}
                >
                  Sua operação profissional começa aqui
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha("#fff", 0.8),
                    lineHeight: 1.4,
                    textShadow: `0 1px 4px ${alpha("#000", 0.1)}`,
                  }}
                >
                  Otimize entregas e integre com iFood, Uber Eats e mais
                </Typography>
              </Box>
            </Box>
          </motion.div>

          {/* Essential — inline chips */}
          <motion.div variants={fadeUp}>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                >
                  Essencial
                </Typography>
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.15,
                    borderRadius: "5px",
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.error.main,
                      fontSize: "0.6rem",
                      letterSpacing: "0.03em",
                    }}
                  >
                    OBRIGATÓRIO
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {essentialConfigs.map((c, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: "10px",
                      border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
                      bgcolor: alpha(theme.palette.error.main, 0.03),
                    }}
                  >
                    <c.icon
                      sx={{
                        fontSize: 16,
                        color: theme.palette.error.main,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: "0.8rem",
                      }}
                    >
                      {c.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </motion.div>

          {/* Advanced — pill grid */}
          <motion.div variants={fadeUp}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                >
                  Recursos
                </Typography>
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.15,
                    borderRadius: "5px",
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.success.main,
                      fontSize: "0.6rem",
                      letterSpacing: "0.03em",
                    }}
                  >
                    DISPONÍVEL
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {advancedFeatures.map((f, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: "10px",
                      border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                      bgcolor: alpha(theme.palette.success.main, 0.03),
                    }}
                  >
                    <f.icon
                      sx={{
                        fontSize: 16,
                        color: theme.palette.success.main,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: "0.8rem",
                      }}
                    >
                      {f.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </motion.div>

          {/* Time hint — inline */}
          <motion.div variants={fadeUp}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 2,
                textAlign: "center",
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Configuração rápida — apenas 5 minutos
            </Typography>
          </motion.div>
        </motion.div>
      </ModalContent>

      <ModalFooter>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleSetupNow}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
          sx={{
            py: 1.5,
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            bgcolor: theme.palette.primary.main,
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
            },
          }}
        >
          Configurar Agora
        </Button>
      </ModalFooter>
    </AnimatedModal>
  );
};

export default DeliveryCenterSetupModal;
